import * as React from 'react';
import classnames from 'classnames';

import Button from '@components/Button';
import Input from '@components/Input';
import ModalBase from '@components/ModalBase';
import Sortable from '@components/Sortable';

import CyberdyneValidator from '@utils/cyberdyneValidator';
import ErrorMessageGenerator from '@utils/ErrorMessageGenerator';

import './style.scss';

interface IPickListOptionsProps {
  show: boolean;
  onClose: () => void;
  options: string[];
  onSubmit: (updatedOptions: string[]) => void;
  LimitPicklistOptionsCount?: boolean;
}

interface IPickListOptionsState {
  options: string[];
  error: IFieldValidation[];
}

const cyberdyneValidator = new CyberdyneValidator();
const errorMessageGenerator = new ErrorMessageGenerator();

export default class PickListOptions extends React.Component<IPickListOptionsProps, IPickListOptionsState> {
  constructor(props: IPickListOptionsProps) {
    super(props);

    this.state = this.getEmptyState();
  }

  componentDidUpdate(prevProps: Readonly<IPickListOptionsProps>) {
    const { options, show } = this.props;

    if (show !== prevProps.show || options !== prevProps.options) {
      if (show) {
        if (options) {
          this.setState({
            ...this.getEmptyState(),
            options,
            error: options.map(() => this.getEmptyErrorState()),
          });
        } else {
          this.setState(this.getEmptyState());
        }
      }
    }
  }

  getEmptyErrorState = (): IFieldValidation => ({
    errorMessage: '',
    errorState: null,
  });

  getEmptyState = (): IPickListOptionsState => ({
    options: [],
    error: [],
  });

  deleteOption = (optionIndex: number) => {
    this.setState((prevState) => ({
      options: [...prevState.options.slice(0, optionIndex), ...prevState.options.slice(optionIndex + 1)],
    }));
  };

  onOptionChange = (e: React.SyntheticEvent<HTMLInputElement>, optionIndex: number) => {
    const newValue = e.currentTarget.value;

    this.setState((prevState) => ({
      options: [...prevState.options.slice(0, optionIndex), newValue, ...prevState.options.slice(optionIndex + 1)],
    }));
  };

  onSortEnd = (updatedOptions: string[]) => {
    this.setState((prevState) => ({
      options: updatedOptions,
    }));
  };

  onAddOption = () => {
    if (this.state.options.length === 15 && this.props.LimitPicklistOptionsCount) {
      const error = this.getEmptyErrorState();
      error.errorMessage = 'At Most 15 values are allowed for this PickList.';
      error.errorState = IValidationState.ERROR;
      this.setState({
        error: [error],
      });
      return;
    }
    this.setState((prevState) => ({
      options: [...prevState.options, ''],
    }));
  };

  validateForm = () => {
    let isValid = true;
    const { options } = this.state;
    const error = options.map((option) => this.getEmptyErrorState());

    options.forEach((option, index) => {
      if (cyberdyneValidator.isEmptyString(option)) {
        error[index].errorState = IValidationState.ERROR;
        error[index].errorMessage = errorMessageGenerator.getMandatoryFieldMessage('Option');

        isValid = false;
      } else if (cyberdyneValidator.isStringGreaterThanLength(option, 300)) {
        error[index].errorState = IValidationState.ERROR;
        error[index].errorMessage = errorMessageGenerator.getCharacterLimitMessage('Option', 300);

        isValid = false;
      } else if (options.findIndex((opt) => opt.toLowerCase() === option.toLowerCase()) !== index) {
        error[index].errorState = IValidationState.ERROR;
        error[index].errorMessage = 'Option Should be unique';

        isValid = false;
      }
    });

    this.setState({
      error,
    });

    return isValid;
  };

  onClose = () => {
    this.props.onClose();
  };

  onSubmit = () => {
    if (this.validateForm()) {
      this.props.onSubmit(this.state.options);
    }
  };

  renderPickListOption = (option: string, optionIndex: number) => (
    <div key={optionIndex} className={classnames('picklist-options__option')}>
      <Input
        width={3}
        field={{
          label: '',
          type: CustomFieldType.TEXT,
          value: option,
          isRequired: false,
        }}
        error={this.state.error[optionIndex]}
        name="option"
        onChange={(e) => this.onOptionChange(e, optionIndex)}
      />
      <a onClick={() => this.deleteOption(optionIndex)} />
    </div>
  );

  renderBody = () => {
    const { options } = this.state;
    const doOptionsExist = options && options.length > 0;

    return (
      <div className={classnames('picklist-options__body')}>
        <h5>The picklist field type requires at least once picklist value.</h5>
        <br />
        <h5>Picklist Options</h5>
        {doOptionsExist && (
          <Sortable sortableItems={options} onSortEnd={this.onSortEnd}>
            {options.map((option, index) => this.renderPickListOption(option, index))}
          </Sortable>
        )}
        <a className="picklist-options__add" onClick={this.onAddOption}>
          <i />
          Add Value
        </a>
      </div>
    );
  };

  renderFooter = () => (
    <div>
      <Button bsStyle={ButtonStyle.DEFAULT} onClick={this.onClose}>
        Cancel
      </Button>
      <Button bsStyle={ButtonStyle.PRIMARY} onClick={this.onSubmit}>
        Save
      </Button>
    </div>
  );

  render() {
    return (
      <ModalBase
        show={this.props.show}
        onClose={this.props.onClose}
        titleElement="Manage Picklist"
        bodyElement={this.renderBody()}
        footerElement={this.renderFooter()}
        className={classnames('picklist-options')}
      />
    );
  }
}
